import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG } from '../config';

// ============================================
// Supabase Client Instances
// ============================================

export const supabase: SupabaseClient = createClient(
  SUPABASE_CONFIG.url,
  SUPABASE_CONFIG.anonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'ob-workflow-auth',
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
    global: {
      headers: {
        'x-client-info': 'ob-workflow-pwa',
      },
    },
  }
);

// Separate client for invite operations (doesn't persist session)
export const inviteClient: SupabaseClient = createClient(
  SUPABASE_CONFIG.url, 
  SUPABASE_CONFIG.anonKey, 
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'ob-workflow-invite-client',
    },
  }
);

// ============================================
// Database Table Names
// ============================================

export const TABLES = {
  USERS: 'users',
  PROJECTS: 'projects',
  TASKS: 'tasks',
  MESSAGES: 'messages',
  NOTIFICATIONS: 'notifications',
  TIMELINE: 'timeline',
} as const;

// ============================================
// Column Mappings (camelCase to snake_case)
// ============================================

export const COLUMN_MAPS = {
  project: {
    toDb: (data: any) => ({
      id: data.id,
      name: data.name,
      description: data.description,
      owner_id: data.ownerId,
      member_ids: data.memberIds,
      deadline: data.deadline,
      status: data.status,
      created_at: data.createdAt,
    }),
    fromDb: (data: any) => ({
      id: data.id,
      name: data.name,
      description: data.description,
      ownerId: data.owner_id,
      memberIds: data.member_ids || [],
      createdAt: data.created_at,
      deadline: data.deadline,
      status: data.status,
    }),
  },
  task: {
    toDb: (data: any) => ({
      id: data.id,
      project_id: data.projectId,
      title: data.title,
      description: data.description,
      status: data.status,
      assignee_id: data.assigneeId,
      deadline: data.deadline,
      estimate_hours: data.estimateHours,
      priority: data.priority,
      checklist: data.checklist,
    }),
    fromDb: (data: any) => ({
      id: data.id,
      projectId: data.project_id,
      title: data.title,
      description: data.description,
      status: data.status,
      assigneeId: data.assignee_id,
      deadline: data.deadline,
      estimateHours: data.estimate_hours,
      priority: data.priority,
      checklist: data.checklist || [],
    }),
  },
  notification: {
    toDb: (data: any) => ({
      id: data.id,
      user_id: data.userId,
      title: data.title,
      message: data.message,
      is_read: data.isRead,
      timestamp: data.timestamp,
      link: data.link,
    }),
    fromDb: (data: any) => ({
      id: data.id,
      userId: data.user_id,
      title: data.title,
      message: data.message,
      isRead: data.is_read,
      timestamp: Number(data.timestamp),
      link: data.link,
    }),
  },
  timeline: {
    toDb: (data: any) => ({
      id: data.id,
      project_id: data.projectId,
      user_id: data.userId,
      user_name: data.userName,
      action: data.type,
      details: data.details,
      timestamp: data.timestamp,
    }),
    fromDb: (data: any) => ({
      id: data.id,
      projectId: data.project_id,
      userId: data.user_id,
      userName: data.user_name,
      type: data.action,
      details: data.details,
      timestamp: Number(data.timestamp),
    }),
  },
  message: {
    // Messages table uses snake_case like other tables
    toDb: (data: any) => ({
      id: data.id,
      project_id: data.projectId,
      user_id: data.senderId,
      text: data.content,
      timestamp: data.timestamp,
    }),
    fromDb: (data: any) => ({
      id: data.id,
      projectId: data.project_id,
      senderId: data.user_id,
      content: data.text,
      timestamp: Number(data.timestamp),
    }),
  },
} as const;
